import React, { useMemo } from 'react'
import { onError } from '@apollo/client/link/error'
import { setContext } from '@apollo/client/link/context'
import {
  getAccessTokenRefreshedIfPossible,
  setTokens,
  setUser,
} from '@/hooks/useAuth/zustand'
import { toast } from '@/hooks/useToast'
import { configs } from '@/configs'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { HttpLink } from '@apollo/client/link/http'
import { createClient } from 'graphql-ws'
import {
  ApolloClient,
  InMemoryCache,
  from,
  // HttpLink,
  split,
  NormalizedCacheObject,
  ApolloLink,
  ApolloProvider,
} from '@apollo/client'
import { getMainDefinition } from '@apollo/client/utilities'
import { authRequestLink, errorLink, handleResponseLink } from './links'
import { MenuProvider } from '@/contexts/MenuContext'
import { customFetchOld } from './utils'
// import { httpUploadLink, link } from '@/contexts/ApolloContext/links'
import { createUploadLink } from 'apollo-upload-client'

function fetchWithTimeout(
  uri: RequestInfo | URL,
  options: RequestInit = {},
  time: number,
) {
  return new Promise<Response>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error('Request timed out.'))
    }, time)
    fetch(uri, options).then(
      (response) => {
        clearTimeout(timer)
        resolve(response)
      },
      (err) => {
        clearTimeout(timer)
        reject(err)
      },
    )
  })
}

// export const httpUploadLink = createUploadLink({
//   uri: `${configs.BACK_URL}/graphql`,
//   fetch: typeof window === 'undefined' ? global.fetch : customFetchOld,
// })

const graphqlUrl = `${configs.BACK_URL}/graphql`

function createIsomorphLink() {
  const httpLink = createUploadLink({
    uri: graphqlUrl,
    credentials: 'same-origin',
    fetch: (uri, options) => {
      if (typeof window === 'undefined') return global.fetch(uri, options)
      const timeoutFromHeader =
        options?.headers &&
        'x-timeout' in options.headers &&
        options.headers['x-timeout']
      const timeout = timeoutFromHeader
        ? Number(timeoutFromHeader)
        : Number(configs.REQUEST_TIMEOUT)
      if ((options as RequestInit & { useUpload?: boolean })?.useUpload) {
        return customFetchOld(uri, options)
      }
      return fetchWithTimeout(uri, options, timeout)
    },
  })

  const getWsLink = () => {
    const wsLink = new GraphQLWsLink(
      createClient({
        url: `${configs.BACK_SOCKET}/graphql`,
        connectionParams: async () => {
          if (typeof window === 'undefined') return {}

          const token = await getAccessTokenRefreshedIfPossible()
          const Authorization = token && `bearer ${token}`

          if (!Authorization) return {}

          return { Authorization }
        },
      }),
    )

    return wsLink
  }

  const connectionLink =
    typeof window === 'undefined'
      ? httpLink
      : split(
          ({ query }) => {
            const definition = getMainDefinition(query)
            return (
              definition.kind === 'OperationDefinition' &&
              definition.operation === 'subscription'
            )
          },
          getWsLink(),
          httpLink,
        )

  return connectionLink
}

export const apolloCache = new InMemoryCache()

export const client = new ApolloClient({
  ssrMode: typeof window === 'undefined',
  cache: apolloCache,
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    },
    query: {
      fetchPolicy: 'cache-first',
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  },
  link: from([
    // Auth
    authRequestLink,
    errorLink,
    handleResponseLink,
    createIsomorphLink(),
    // httpUploadLink,
  ]),
})

export const ApolloContext = ({ children }: { children: React.ReactNode }) => {
  return (
    <ApolloProvider client={client}>
      <MenuProvider>{children}</MenuProvider>
    </ApolloProvider>
  )
}
